import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import { validateDcfFile } from "../../../../../domain/provision/provision.rules";
import { DCF_ALLOWED_EXTENSIONS, DCF_MAX_SIZE_BYTES } from "../../../../../domain/provision/provision.types";
import { api } from "./Provision.helper";
import useStyles from "./Provision.styles";

const MAX_SIZE_MB = Math.round(DCF_MAX_SIZE_BYTES / (1024 * 1024));
const ACCEPT_ATTR = DCF_ALLOWED_EXTENSIONS.join(",");

/**
 * Upload DCF (claude-code-UI-prompt.md §Screen structure 2, DFXX11): drag-and-drop + choose
 * files; enforces PDF/Excel/Word, <= 25MB.
 *
 * The file is uploaded direct to the Document Helper and only its reference is persisted via our
 * API (see uploadDcfDocument) — so unlike most validation in this app, validateDcfFile below is
 * the REAL enforcement of the type/size limits, not a convenience ahead of a server check. The
 * API never receives the bytes and can only validate the reported extension.
 */
const UploadDcfSection = ({ loanId, editable, onUploaded }) => {
  const classes = useStyles();
  // The Document Helper carries its own bearer token rather than going through apis.js's shared
  // axios config, so it needs the oidc user passed explicitly — same as QuotesEditor.jsx.
  const oidcUser = useSelector((state) => state.oidc && state.oidc.user);
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const message = validateDcfFile(file);
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const document = await api.uploadDcfDocument(loanId, file, oidcUser);
      onUploaded(document);
    } catch (err) {
      setError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className={classes.card} style={{ marginBottom: 0 }}>
      <div className={classes.cardHeader}>
        <span className={classes.cardHeaderTitle}>Upload DCF</span>
      </div>
      <div className={classes.cardBody}>
        <p className={classes.subtle} style={{ marginTop: 0 }}>
          Upload Discounted Cash Flow (DCF) files related to this provision.
        </p>

        <div
          className={`${classes.dropzone} ${hover ? classes.dropzoneHover : ""}`}
          onDragOver={(event) => {
            stop(event);
            if (editable) setHover(true);
          }}
          onDragLeave={(event) => {
            stop(event);
            setHover(false);
          }}
          onDrop={(event) => {
            stop(event);
            setHover(false);
            if (!editable || uploading) return;
            const file = event.dataTransfer.files && event.dataTransfer.files[0];
            handleFile(file);
          }}
        >
          <CloudUploadIcon style={{ fontSize: 40, color: "var(--color-primary-text)", opacity: 0.85 }} />
          {uploading ? (
            <div style={{ marginTop: 10 }}>
              <CircularProgress size={20} />
            </div>
          ) : (
            <>
              <div style={{ marginTop: 10 }}>Drag and drop files here</div>
              <div className={classes.subtle} style={{ margin: "8px 0" }}>
                or
              </div>
              <A_Button
                label="Choose Files"
                onClick={() => inputRef.current && inputRef.current.click()}
                disabled={!editable}
              />
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            style={{ display: "none" }}
            onChange={(event) => {
              const file = event.target.files && event.target.files[0];
              event.target.value = "";
              handleFile(file);
            }}
          />
        </div>

        <div className={classes.filehint}>
          <span>Allowed file types: PDF, Excel, Word</span>
          <span>Max file size: {MAX_SIZE_MB}MB</span>
        </div>
        {error && (
          <div className={classes.validationSummary} style={{ marginTop: 12, marginBottom: 0 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

UploadDcfSection.propTypes = {
  loanId: PropTypes.string.isRequired,
  editable: PropTypes.bool,
  onUploaded: PropTypes.func.isRequired,
};

export default UploadDcfSection;
